/**
 * Schema.org JSON-LD builders (pure functions, unit tested in
 * tests/specs/seo/structured-data.spec.js).
 */

import { PUBLISHED_LANGUAGES, X_DEFAULT_LANGUAGE, toCanonicalUrl } from "./seo"

export const SITE_NAME = "Attijari MDM"
// Name also used by the site (titles, Drupal site name).
const SITE_ALTERNATE_NAME = "Attijarimdm"

// The portal is published by the bank; its logo is the bank symbol (square
// PNG, as recommended by Google: /logomdm.png is a WebP served as .png).
const PUBLISHER = {
	"@type": "BankOrCreditUnion",
	name: "Attijariwafa bank",
	url: "https://www.attijariwafabank.com",
}
const LOGO_PATH = "/icons/icon-512x512.png"
const LOGO_SIZE = 512

/**
 * WebSite + publisher graph, rendered on the language homes. The values are
 * the same on /fr, /ar and /en: one entity per @id (the default-language home
 * is the site URL, as for x-default).
 */
export const buildHomeGraph = ({ origin }) => {
	if (!origin) {
		return null
	}
	const publisherId = `${origin}/#publisher`
	return {
		"@context": "https://schema.org",
		"@graph": [
			{
				...PUBLISHER,
				"@id": publisherId,
				logo: {
					"@type": "ImageObject",
					url: `${origin}${LOGO_PATH}`,
					width: LOGO_SIZE,
					height: LOGO_SIZE,
				},
			},
			{
				"@type": "WebSite",
				"@id": `${origin}/#website`,
				name: SITE_NAME,
				alternateName: SITE_ALTERNATE_NAME,
				url: `${origin}/${X_DEFAULT_LANGUAGE}`,
				inLanguage: PUBLISHED_LANGUAGES,
				publisher: { "@id": publisherId },
			},
		],
	}
}

/**
 * BreadcrumbList from the Drupal breadcrumb (ancestors, e.g.
 * [{ url: "/fr", text: "Accueil" }]) followed by the current page.
 * Returns null when there is no ancestor or no usable URL.
 */
export const buildBreadcrumbList = ({ crumbs, current, origin }) => {
	const cleanName = (value) => `${value ?? ""}`.replace(/\s+/g, " ").trim()
	const currentName = cleanName(current?.name)
	if (!origin || !current?.url || !currentName) {
		return null
	}
	const ancestors = (Array.isArray(crumbs) ? crumbs : [])
		.map((crumb) => ({
			name: cleanName(crumb?.text),
			path: `${crumb?.url ?? ""}`.trim(),
		}))
		// Only real pages of the site: published language prefix, no Drupal
		// untranslated fallback (/xx/node/ID), no "<none>" placeholder.
		.filter(
			({ name, path }) =>
				name &&
				name !== "<none>" &&
				PUBLISHED_LANGUAGES.some(
					(lang) => path === `/${lang}` || path.startsWith(`/${lang}/`)
				) &&
				!/^\/[^/]+\/node\/\d+\/?$/.test(path)
		)
		.map(({ name, path }) => ({ name, url: toCanonicalUrl(path, origin) }))
		.filter((crumb) => crumb.url && crumb.url !== current.url)
	if (!ancestors.length) {
		return null
	}
	return {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: [...ancestors, { name: currentName, url: current.url }].map(
			(item, index) => ({
				"@type": "ListItem",
				position: index + 1,
				name: item.name,
				item: item.url,
			})
		),
	}
}

/**
 * Serializes JSON-LD for an inline <script>: "<" is escaped so that content
 * coming from the CMS can never close the script element.
 */
export const serializeJsonLd = (data) => JSON.stringify(data).replace(/</g, "\\u003c")
